import { useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Play, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { addDays } from "date-fns";

type Step = { name: string; status: "pending" | "running" | "ok" | "fail"; detail?: string; at?: string };

export default function AdminRunBookingTest() {
  const [steps, setSteps] = useState<Step[]>([]);
  const [running, setRunning] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);

  const set = (i: number, patch: Partial<Step>) =>
    setSteps(prev => prev.map((s, idx) => idx === i ? { ...s, ...patch, at: new Date().toISOString() } : s));

  const run = async () => {
    setRunning(true); setBookingId(null); setCheckoutUrl(null);
    const plan: Step[] = [
      { name: "RLS self-test (insert + select + cleanup)", status: "pending" },
      { name: "Acquire slot lock", status: "pending" },
      { name: "Insert pending booking", status: "pending" },
      { name: "Create Stripe Checkout session", status: "pending" },
      { name: "Awaiting payment (test card 4242 4242 4242 4242)", status: "pending" },
      { name: "Webhook reconciles booking → confirmed", status: "pending" },
    ];
    setSteps(plan);

    try {
      // 1) RLS selftest
      set(0, { status: "running" });
      const rls = await supabase.functions.invoke("booking-rls-selftest");
      if (rls.error) throw new Error(`RLS selftest failed: ${rls.error.message}`);
      const allOk = (rls.data as any)?.ok;
      set(0, { status: allOk ? "ok" : "fail", detail: JSON.stringify((rls.data as any)?.checks ?? rls.data) });
      if (!allOk) throw new Error("RLS selftest reported a failing check");

      // Pick the first studio.
      const { data: studio } = await supabase.from("studios").select("id,name").limit(1).maybeSingle();
      if (!studio) throw new Error("No studios configured");

      // Far-future slot to avoid colliding with real bookings.
      const start = addDays(new Date(), 365); start.setUTCHours(5, 0, 0, 0);
      const end = new Date(start.getTime() + 60 * 60 * 1000);
      const sessionToken = `e2e-${crypto.randomUUID()}`;

      // 2) Slot lock
      set(1, { status: "running" });
      const lock = await supabase.rpc("acquire_slot_lock" as any, {
        _studio_id: studio.id, _start: start.toISOString(), _end: end.toISOString(),
        _session_token: sessionToken, _ttl_seconds: 900,
      });
      if (lock.error) throw new Error(`Lock failed: ${lock.error.message}`);
      set(1, { status: "ok", detail: `token=${sessionToken.slice(0, 14)}…` });

      // 3) Insert booking
      set(2, { status: "running" });
      const { data: sess } = await supabase.auth.getSession();
      const uid = sess.session?.user.id ?? null;
      const newId = crypto.randomUUID();
      const ins = await supabase.from("bookings").insert({
        id: newId, studio_id: studio.id,
        start_at: start.toISOString(), end_at: end.toISOString(),
        subtotal: 1, total: 1, amount_paid: 0,
        status: "pending", payment_status: "unpaid",
        user_id: uid, notes: "[admin-e2e] test booking",
      });
      if (ins.error) throw new Error(`Insert failed: ${ins.error.message}`);
      setBookingId(newId);
      set(2, { status: "ok", detail: `id=${newId}` });

      // 4) Checkout session
      set(3, { status: "running" });
      const origin = window.location.origin;
      const co = await supabase.functions.invoke("stripe-checkout-booking", {
        body: {
          booking_id: newId,
          success_url: `${origin}/book/success?e2e=1`,
          cancel_url: `${origin}/admin/run-booking-test?cancelled=1`,
          customer_email: sess.session?.user.email ?? "e2e@example.com",
          customer_name: "E2E Tester",
        },
      });
      if (co.error || !co.data?.url) throw new Error(`Checkout failed: ${co.error?.message ?? co.data?.error ?? "no url"}`);
      setCheckoutUrl(co.data.url);
      set(3, { status: "ok", detail: "session created — popup opened" });
      window.open(co.data.url, "_blank", "noopener");

      // 5) Wait for payment
      set(4, { status: "running", detail: "Use card 4242 4242 4242 4242, any future exp, any CVC, any ZIP" });

      // 6) Poll booking until confirmed
      set(5, { status: "running" });
      const deadline = Date.now() + 5 * 60_000;
      let confirmed = false;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 3000));
        const { data: b } = await supabase.from("bookings")
          .select("status,payment_status,stripe_checkout_session_id").eq("id", newId).maybeSingle();
        if (b?.status === "confirmed" && b.payment_status === "paid") {
          set(4, { status: "ok", detail: `session=${b.stripe_checkout_session_id?.slice(0, 20) ?? ""}…` });
          set(5, { status: "ok", detail: `payment_status=paid, status=confirmed` });
          confirmed = true; break;
        }
      }
      if (!confirmed) {
        set(4, { status: "fail", detail: "Timeout — payment not completed in 5 min" });
        set(5, { status: "fail", detail: "Booking never moved to confirmed/paid" });
        throw new Error("Test timed out waiting for payment confirmation");
      }
      toast.success("End-to-end booking test passed");
    } catch (e) {
      toast.error((e as Error).message);
      setSteps(prev => prev.map(s => s.status === "running" ? { ...s, status: "fail", detail: (e as Error).message } : s));
    } finally { setRunning(false); }
  };

  return (
    <AdminShell title="RUN BOOKING TEST">
      <div className="space-y-4 max-w-3xl">
        <p className="text-sm text-muted-foreground">
          Executes the full booking flow: RLS check → slot lock → booking insert → Stripe Checkout (test mode) → webhook → confirmed booking.
          Use Stripe test card <code className="bg-panel px-1">4242 4242 4242 4242</code> when the popup opens.
        </p>
        <div className="flex gap-2">
          <Button onClick={run} disabled={running}>
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Play className="w-4 h-4 mr-2"/>}
            {running ? "Running…" : "Run end-to-end test"}
          </Button>
          {checkoutUrl && (
            <Button variant="outline" asChild>
              <a href={checkoutUrl} target="_blank" rel="noopener noreferrer">
                Re-open Stripe checkout <ExternalLink className="w-3 h-3 ml-2"/>
              </a>
            </Button>
          )}
          {bookingId && (
            <Button variant="outline" asChild>
              <a href={`/admin/bookings/${bookingId}`} target="_blank" rel="noopener noreferrer">View booking</a>
            </Button>
          )}
        </div>

        <div className="border border-border">
          {steps.length === 0 && (
            <div className="p-6 text-muted-foreground text-sm">No run yet. Hit “Run end-to-end test”.</div>
          )}
          {steps.map((s, i) => (
            <div key={i} className="flex items-start gap-3 p-3 border-t border-border first:border-t-0">
              <div className="mt-0.5">
                {s.status === "ok" && <CheckCircle2 className="w-5 h-5 text-emerald-400"/>}
                {s.status === "fail" && <XCircle className="w-5 h-5 text-rose-400"/>}
                {s.status === "running" && <Loader2 className="w-5 h-5 animate-spin text-amber-400"/>}
                {s.status === "pending" && <div className="w-5 h-5 rounded-full border border-border"/>}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-sm">{i + 1}. {s.name}</div>
                  {s.at && <div className="text-xs text-muted-foreground">{new Date(s.at).toLocaleTimeString()}</div>}
                </div>
                {s.detail && <div className="text-xs text-muted-foreground mt-1 break-all">{s.detail}</div>}
              </div>
              <Badge variant="outline">{s.status}</Badge>
            </div>
          ))}
        </div>
      </div>
    </AdminShell>
  );
}